import React from "react";
import { Check, Info, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { Badge } from "@/components/Badge/Badge";
import { Button } from "@/components/Button/Button";
import ConfirmationDialog from "@/components/ConfirmationDialog/ConfirmationDialog";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import SearchInput from "@/components/SearchInput/SearchInput";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useAccountMappingSection } from "../../hooks/useAccountMappingSection";
import {
  ACCOUNT_FLAG_COPY,
  AI_GRADIENT_RING_CLASSES,
  CROSS_TYPE_NOTICE,
  DO_NOT_MAP_CODE,
  FilterTab,
  FILTER_TABS,
  STATUS_BADGE_CLASSES,
  STATUS_ROW_CLASSES,
  STATUS_LABELS,
} from "../../config/accountMappingDisplay";

/**
 * Radix rejects an empty-string item value, so "no CertainPath account" travels
 * as a sentinel and is turned back into `null` before it reaches the hook.
 */
const CLEAR_MAPPING_VALUE = "__clear__";

/** Matches `DataTableHeader`, so this table reads the same as prospects and customers. */
const HEADER_CELL_CLASSES =
  "h-11 whitespace-nowrap px-4 align-middle font-semibold text-gray-600 dark:text-slate-300";

interface AccountMappingSectionProps {
  companyUuid: string;
}

export const AccountMappingSection: React.FC<AccountMappingSectionProps> = ({
  companyUuid,
}) => {
  const {
    visibleAccounts,
    counts,
    syncedAt,
    isLoading,
    isSaving,
    savingAccountIds,
    isAutoMapping,
    activeTab,
    setActiveTab,
    searchQuery,
    setSearchQuery,
    unmappedCount,
    pendingExclusion,
    confirmExclusion,
    cancelExclusion,
    changeLedgerAccount,
    selectedAccountIds,
    selectedCount,
    isSelectable,
    toggleAccountSelection,
    toggleSelectAllVisible,
    areAllVisibleSelected,
    hasSelectableVisibleRows,
    confirmSelected,
    runAutoMap,
    buildLedgerAccountGroups,
  } = useAccountMappingSection(companyUuid);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <div>
      <ToggleGroup
        className="w-fit justify-start gap-1 rounded-lg border border-gray-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-800"
        onValueChange={(value) => {
          // Radix clears the value when the selected item is clicked again, and
          // a filter row with nothing selected has no meaning here.
          if (value) {
            setActiveTab(value as FilterTab);
          }
        }}
        type="single"
        value={activeTab}
      >
        {FILTER_TABS.map((tab) => (
          <ToggleGroupItem
            className="group h-auto gap-1.5 px-3 py-1.5 font-normal text-gray-500 hover:bg-transparent hover:text-gray-900 data-[state=on]:bg-primary data-[state=on]:text-white dark:text-gray-400 dark:hover:text-white"
            key={tab.id}
            value={tab.id}
          >
            {tab.label}
            <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-gray-100 px-1.5 text-xs text-gray-600 group-data-[state=on]:bg-white group-data-[state=on]:text-gray-900 dark:bg-slate-700 dark:text-gray-300 dark:group-data-[state=on]:bg-white dark:group-data-[state=on]:text-gray-900">
              {counts[tab.id]}
            </span>
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <SearchInput
          className="max-w-sm"
          onChange={setSearchQuery}
          placeholder="Search name, number, or sub-type…"
          value={searchQuery}
        />

        <div className="ml-auto flex items-center gap-2">
          <Button
            className={`rounded-md ${AI_GRADIENT_RING_CLASSES}`}
            disabled={isAutoMapping || isSaving}
            onClick={runAutoMap}
            size="sm"
            variant="ghost"
          >
            <Sparkles />
            {isAutoMapping ? "Mapping…" : "Auto-map"}
          </Button>
          <Button
            className="rounded-md"
            disabled={selectedCount === 0 || isSaving}
            onClick={confirmSelected}
            size="sm"
            variant="default"
          >
            <Check />
            {selectedCount === 0
              ? "Accept selected"
              : `Accept ${selectedCount} selected`}
          </Button>
        </div>
      </div>

      <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
        <table className="w-full text-sm">
          <thead className="[&_tr]:border-b [&_tr]:border-gray-200 dark:[&_tr]:border-slate-700">
            <tr className="bg-gray-50 text-left dark:bg-slate-700/50">
              <th className={`w-10 ${HEADER_CELL_CLASSES}`}>
                <Checkbox
                  aria-label="Select all accounts shown"
                  checked={areAllVisibleSelected}
                  className="cursor-pointer"
                  disabled={!hasSelectableVisibleRows || isSaving}
                  onCheckedChange={toggleSelectAllVisible}
                  size="sm"
                />
              </th>
              <th className={HEADER_CELL_CLASSES}>QuickBooks account</th>
              <th className={`w-1/3 ${HEADER_CELL_CLASSES}`}>
                CertainPath account
              </th>
              <th className={`w-px text-right ${HEADER_CELL_CLASSES}`}>
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleAccounts.map((account) => (
              <tr
                className={`border-b border-gray-100 last:border-b-0 dark:border-slate-700/50 ${STATUS_ROW_CLASSES[account.status]}`}
                key={account.qboAccountId}
              >
                <td className="px-3 py-2.5 align-top">
                  {isSelectable(account) && (
                    <Checkbox
                      aria-label={`Approve mapping for ${account.name}`}
                      checked={selectedAccountIds.has(account.qboAccountId)}
                      className="mt-1 cursor-pointer"
                      disabled={isSaving}
                      onCheckedChange={() =>
                        toggleAccountSelection(account.qboAccountId)
                      }
                      size="sm"
                    />
                  )}
                </td>

                <td className="px-4 py-2.5">
                  <div className="text-gray-900 dark:text-white">
                    {account.name}
                    {account.accountNumber && (
                      <span className="text-gray-500 dark:text-gray-400">
                        {" · "}
                        {account.accountNumber}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {account.accountType ?? "—"} →{" "}
                    {account.accountSubType ?? "—"}
                  </div>
                  {account.isSubAccount && account.fullyQualifiedName && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {account.fullyQualifiedName}
                    </div>
                  )}
                  {account.flags.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                      {account.flags.map((flag) => (
                        <span
                          className={`inline-flex cursor-help items-center gap-1 text-xs ${
                            ACCOUNT_FLAG_COPY[flag].isWarning
                              ? "text-amber-700 dark:text-amber-300"
                              : "text-gray-500 dark:text-gray-400"
                          }`}
                          key={flag}
                          title={ACCOUNT_FLAG_COPY[flag].text}
                        >
                          <Info className="h-3 w-3 shrink-0" />
                          {ACCOUNT_FLAG_COPY[flag].label}
                        </span>
                      ))}
                    </div>
                  )}
                </td>

                <td className="px-3 py-2.5">
                  <Select
                    disabled={isSaving}
                    onValueChange={(value) =>
                      changeLedgerAccount(
                        account.qboAccountId,
                        value === CLEAR_MAPPING_VALUE ? null : value,
                      )
                    }
                    value={account.mapping?.ledgerAccountCode ?? ""}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Only worth offering once there is a mapping to undo. */}
                      {account.mapping && (
                        <SelectItem value={CLEAR_MAPPING_VALUE}>
                          — Clear selection —
                        </SelectItem>
                      )}
                      {buildLedgerAccountGroups(account.ledgerAccountType).map(
                        (group) => (
                          <SelectGroup key={group.label}>
                            <SelectLabel>{group.label}</SelectLabel>
                            {group.categories.map((category) => (
                              <SelectItem
                                key={category.code}
                                value={category.code}
                              >
                                {category.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  {account.mapping?.isCrossType &&
                    account.mapping.ledgerAccountCode !== DO_NOT_MAP_CODE && (
                      <span
                        className="mt-1 inline-flex cursor-help items-center gap-1 text-xs text-gray-500 dark:text-gray-400"
                        title={CROSS_TYPE_NOTICE.text}
                      >
                        <Info className="h-3 w-3 shrink-0" />
                        {CROSS_TYPE_NOTICE.label}
                      </span>
                    )}
                </td>

                <td className="whitespace-nowrap px-3 py-2.5 text-right">
                  {/* The old status is stale the moment the row changes, so it
                      gives way to the spinner rather than sitting there wrong. */}
                  {savingAccountIds.has(account.qboAccountId) ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                      <Loader2
                        className="h-3.5 w-3.5 shrink-0 animate-spin"
                        strokeWidth={1.75}
                      />
                      Saving
                    </span>
                  ) : (
                    <Badge
                      className={`gap-1 whitespace-nowrap ${STATUS_BADGE_CLASSES[account.status]}`}
                    >
                      {STATUS_LABELS[account.status]}
                      {account.status === "ai_mapped" && (
                        <Sparkles
                          className="h-3.5 w-3.5 shrink-0"
                          strokeWidth={1.75}
                        />
                      )}
                    </Badge>
                  )}
                </td>
              </tr>
            ))}
            {visibleAccounts.length === 0 && (
              <tr>
                <td
                  className="py-10 text-center text-sm text-gray-500 dark:text-gray-400"
                  colSpan={4}
                >
                  No accounts in this view.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500 dark:text-gray-400">
        <span>
          {unmappedCount === 0
            ? "Every account is mapped."
            : `${unmappedCount} account${unmappedCount === 1 ? "" : "s"} still need a CertainPath account.`}
        </span>
        {syncedAt && (
          <span className="flex items-center gap-1.5 text-xs">
            <RefreshCw className="h-3.5 w-3.5" />
            Synced {new Date(syncedAt).toLocaleDateString()}
          </span>
        )}
      </div>

      <ConfirmationDialog
        cancelMessage="Cancel"
        confirmMessage="Exclude"
        dialogInstructionFinalQuestion="Are you sure you want to exclude it?"
        dialogInstructionItems={[]}
        dialogInstructionTitle={`${pendingExclusion?.name} carries a balance and will be left out of every KPI.`}
        dialogTitleContent="Exclude account"
        isOpen={pendingExclusion !== null}
        onClose={cancelExclusion}
        onConfirm={confirmExclusion}
      />
    </div>
  );
};
